import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { onSignOut } from '../lib/signOutReset';
import { SOCIAL_PARAMETER_NAMES, SocialParameterName } from '../constants/socialParameters';
import { PointsDistribution, calculateXpPerPoint } from '../utils/socialActivityXP';

export interface SocialParameter {
  id: string;
  user_id: string;
  parameter_name: SocialParameterName;
  level: number;
  xp: number;
  current_level_xp: number;
  xp_to_next_level: number;
  created_at: string;
  updated_at: string;
}

export interface SocialParameterActivity {
  id: string;
  user_id: string;
  parameter_name: SocialParameterName;
  points_allocated: number;
  base_xp: number;
  scaled_xp: number;
  multiplier: number;
  source_type: 'activity' | 'program' | 'external';
  source_activity_id: string | null;
  created_at: string;
}

interface SocialParametersState {
  parameters: SocialParameter[];
  activities: SocialParameterActivity[];
  isLoading: boolean;
  error: string | null;

  fetchParameters: () => Promise<void>;
  fetchActivities: () => Promise<void>;
  initializeParameters: () => Promise<void>;
  addXP: (
    parameterName: SocialParameterName,
    points: number,
    sourceType: 'activity' | 'program' | 'external',
    sourceActivityId?: string,
    socialDomainLevel?: number
  ) => Promise<void>;
  distributePoints: (
    distribution: PointsDistribution,
    sourceType: 'activity' | 'program' | 'external',
    sourceActivityId?: string,
    socialDomainLevel?: number
  ) => Promise<{ totalXP: number; parameterXP: { parameter: string; xp: number }[] }>;
  getParameter: (name: SocialParameterName) => SocialParameter | undefined;
  getTotalLevel: () => number;
  getAverageLevel: () => number;
  getStrongestParameter: () => SocialParameter | null;
  getWeakestParameter: () => SocialParameter | null;
  clearStore: () => void;
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

/** An action's failure — shown only while the account it ran for is still signed in. */
function failed(at: number, error: unknown) {
  if (at === epoch) useSocialParametersStore.setState({ error: (error as Error).message });
}

export const useSocialParametersStore = create<SocialParametersState>((set, get) => ({
  parameters: [],
  activities: [],
  isLoading: false,
  error: null,

  fetchParameters: async () => {
    const at = epoch;
    set({ isLoading: true, error: null });
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) {
        set({ isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('social_parameters')
        .select('*')
        .eq('user_id', user.id)
        .order('parameter_name');
      // Signed out while it read: its parameters don't land, and none are made for the next account.
      if (at !== epoch) return;

      if (error) throw error;

      if (!data || data.length === 0) {
        await get().initializeParameters();
        return;
      }

      set({ parameters: data as SocialParameter[], isLoading: false });
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message, isLoading: false });
    }
  },

  fetchActivities: async () => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      const { data, error } = await supabase
        .from('social_parameter_activities')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);
      if (at !== epoch) return; // signed out while it read

      if (error) throw error;
      set({ activities: (data ?? []) as SocialParameterActivity[] });
    } catch (error) {
      failed(at, error);
    }
  },

  initializeParameters: async () => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      const { error: initError } = await supabase.rpc('initialize_social_parameters', {
        p_user_id: user.id
      });

      if (initError) {
        const newParams = SOCIAL_PARAMETER_NAMES.map(name => ({
          user_id: user.id,
          parameter_name: name,
          level: 1,
          xp: 0,
          current_level_xp: 0,
          xp_to_next_level: 100
        }));

        const { error: insertError } = await supabase
          .from('social_parameters')
          .upsert(newParams, { onConflict: 'user_id,parameter_name' });

        if (insertError) throw insertError;
      }

      const { data, error } = await supabase
        .from('social_parameters')
        .select('*')
        .eq('user_id', user.id)
        .order('parameter_name');

      if (at !== epoch) return; // signed out while it ran
      if (error) throw error;

      set({ parameters: data as SocialParameter[], isLoading: false });
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message, isLoading: false });
    }
  },

  addXP: async (parameterName, points, sourceType, sourceActivityId, socialDomainLevel = 1) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      const xpPerPoint = calculateXpPerPoint(socialDomainLevel);
      const scaledXP = points * xpPerPoint;
      const baseXP = points * 50;

      const { error: activityError } = await supabase
        .from('social_parameter_activities')
        .insert({
          user_id: user.id,
          parameter_name: parameterName,
          points_allocated: points,
          base_xp: baseXP,
          scaled_xp: scaledXP,
          multiplier: xpPerPoint / 50,
          source_type: sourceType,
          source_activity_id: sourceActivityId || null
        });

      if (activityError) throw activityError;
      // ⚠️⚠️ Signed out while it saved: the parameter is found by NAME in what this store
      // holds NOW — the next account's, which would take this account's XP.
      if (at !== epoch) return;

      const currentParam = get().parameters.find(p => p.parameter_name === parameterName);
      if (currentParam) {
        const newXP = currentParam.xp + scaledXP;

        const { error: updateError } = await supabase
          .from('social_parameters')
          .update({ xp: newXP })
          .eq('id', currentParam.id);

        if (updateError) throw updateError;
      }

      await get().fetchParameters();
    } catch (error) {
      failed(at, error);
    }
  },

  distributePoints: async (distribution, sourceType, sourceActivityId, socialDomainLevel = 1) => {
    const at = epoch;
    const results: { parameter: string; xp: number }[] = [];
    let totalXP = 0;

    for (const [parameterName, points] of Object.entries(distribution)) {
      // ⚠️⚠️ Each addXP asks who is signed in WHEN it is called — after a sign-out, the
      // next account would take the rest of this distribution.
      if (at !== epoch) break;
      if (points > 0) {
        const xpPerPoint = calculateXpPerPoint(socialDomainLevel);
        const xp = points * xpPerPoint;
        results.push({ parameter: parameterName, xp });
        totalXP += xp;

        await get().addXP(
          parameterName as SocialParameterName,
          points,
          sourceType,
          sourceActivityId,
          socialDomainLevel
        );
      }
    }

    return { totalXP, parameterXP: results };
  },

  getParameter: (name) => {
    return get().parameters.find(p => p.parameter_name === name);
  },

  getTotalLevel: () => {
    const params = get().parameters;
    if (params.length === 0) return 0;
    return params.reduce((sum, p) => sum + p.level, 0);
  },

  getAverageLevel: () => {
    const params = get().parameters;
    if (params.length === 0) return 1;
    return Math.round(params.reduce((sum, p) => sum + p.level, 0) / params.length);
  },

  getStrongestParameter: () => {
    const params = get().parameters;
    if (params.length === 0) return null;
    return params.reduce((max, p) => (p.xp > max.xp ? p : max), params[0]);
  },

  getWeakestParameter: () => {
    const params = get().parameters;
    if (params.length === 0) return null;
    return params.reduce((min, p) => (p.xp < min.xp ? p : min), params[0]);
  },

  /** Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does not reload the page. */
  clearStore: () => {
    epoch += 1;
    set({ parameters: [], activities: [], isLoading: false, error: null });
  },
}));

onSignOut(() => useSocialParametersStore.getState().clearStore());
